<script setup lang="ts">
import { type Client, type DriverConfig, clientsApi, driverConfigsApi } from '@/api/entities'

definePage({
  meta: {
    action: 'read',
    subject: 'Client',
  },
})

const route = useRoute()
const router = useRouter()

const clientId = computed(() => route.params.id as string)
const client = ref<Client | null>(null)
const driverConfigs = ref<DriverConfig[]>([])
const loading = ref(true)
const configsLoading = ref(false)

// 클라이언트 정보 로드
const loadClient = async () => {
  loading.value = true
  try {
    const response = await clientsApi.getClient(clientId.value)
    if (response.success && response.data)
      client.value = response.data
  }
  catch (error) {
    console.error('고객사 정보 로드 실패:', error)
  }
  finally {
    loading.value = false
  }
}

// 드라이버 설정 로드
const loadDriverConfigs = async () => {
  configsLoading.value = true
  try {
    const response = await driverConfigsApi.listDriverConfigs(clientId.value)
    if (response.success && response.data) {
      const data = response.data as any

      driverConfigs.value = data.data || data
    }
  }
  catch (error) {
    console.error('드라이버 설정 로드 실패:', error)
  }
  finally {
    configsLoading.value = false
  }
}

// 초기화
onMounted(() => {
  loadClient()
  loadDriverConfigs()
})

// 수정 기능 - 추후 구현 예정
const goToEdit = () => {
  console.log('수정 기능은 추후 구현 예정입니다.')
}

// 삭제
const deleteClient = async () => {
  if (!confirm('정말로 이 고객사를 삭제하시겠습니까?'))
    return

  try {
    const response = await clientsApi.deleteClient(clientId.value)
    if (response.success)
      router.push({ name: 'review-client' })
  }
  catch (error) {
    console.error('고객사 삭제 실패:', error)
  }
}

// 드라이버 설정 테이블 헤더
const configHeaders = [
  { title: 'ID', key: 'id' },
  { title: '채널명', key: 'name' },
  { title: '다운로드 드라이버', key: 'download_driver_id' },
  { title: '업로드 드라이버', key: 'upload_driver_id' },
  { title: '작업', key: 'actions', sortable: false },
]
</script>

<template>
  <div>
    <!-- 로딩 상태 -->
    <VCard
      v-if="loading"
      class="mb-6"
    >
      <VCardText class="text-center pa-6">
        <VProgressCircular indeterminate />
        <p class="mt-4">
          고객사 정보를 불러오는 중...
        </p>
      </VCardText>
    </VCard>

    <!-- 고객사 정보 -->
    <template v-else-if="client">
      <VCard class="mb-6">
        <VCardTitle class="d-flex align-center justify-space-between pa-6">
          <div class="d-flex align-center gap-4">
            <VIcon
              icon="tabler-building"
              size="32"
            />
            <div>
              <h4 class="text-h4">
                {{ client.name }}
              </h4>
              <VChip
                :color="client.type === 'widget' ? 'success' : 'info'"
                size="small"
                class="mt-1"
              >
                {{ client.type === 'widget' ? '위젯' : '이관' }}
              </VChip>
            </div>
          </div>
          <div class="d-flex gap-2">
            <VBtn
              color="primary"
              variant="outlined"
              @click="goToEdit"
            >
              <VIcon
                start
                icon="tabler-edit"
              />
              수정
            </VBtn>
            <VBtn
              color="error"
              variant="outlined"
              @click="deleteClient"
            >
              <VIcon
                start
                icon="tabler-trash"
              />
              삭제
            </VBtn>
          </div>
        </VCardTitle>

        <VDivider />

        <VCardText>
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VList>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-database" />
                  </template>
                  <VListItemTitle>DB명</VListItemTitle>
                  <VListItemSubtitle>{{ client.target_database_hive }}</VListItemSubtitle>
                </VListItem>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-world" />
                  </template>
                  <VListItemTitle>PC 도메인</VListItemTitle>
                  <VListItemSubtitle>{{ client.host }}</VListItemSubtitle>
                </VListItem>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-star" />
                  </template>
                  <VListItemTitle>별점 기준</VListItemTitle>
                  <VListItemSubtitle>
                    <VRating
                      :model-value="client.score"
                      readonly
                      density="compact"
                    />
                  </VListItemSubtitle>
                </VListItem>
              </VList>
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VList>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-file-text" />
                  </template>
                  <VListItemTitle>텍스트 리뷰 이관</VListItemTitle>
                  <VListItemSubtitle>
                    <VIcon
                      :icon="client.text === 'Y' ? 'tabler-check' : 'tabler-x'"
                      :color="client.text === 'Y' ? 'success' : 'error'"
                    />
                  </VListItemSubtitle>
                </VListItem>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-photo" />
                  </template>
                  <VListItemTitle>포토 리뷰 이관</VListItemTitle>
                  <VListItemSubtitle>
                    <VIcon
                      :icon="client.photo === 'Y' ? 'tabler-check' : 'tabler-x'"
                      :color="client.photo === 'Y' ? 'success' : 'error'"
                    />
                  </VListItemSubtitle>
                </VListItem>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-message" />
                  </template>
                  <VListItemTitle>예상 리뷰 수</VListItemTitle>
                  <VListItemSubtitle>{{ client.expected_review_count?.toLocaleString() || 0 }}개</VListItemSubtitle>
                </VListItem>
              </VList>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <!-- 드라이버 설정 -->
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between pa-6">
          <span>채널 설정</span>
          <VBtn
            color="primary"
            size="small"
          >
            <VIcon
              start
              icon="tabler-plus"
            />
            채널 추가
          </VBtn>
        </VCardTitle>

        <VDivider />

        <VCardText>
          <VDataTable
            :headers="configHeaders"
            :items="driverConfigs"
            :loading="configsLoading"
          >
            <template #item.actions>
              <div class="d-flex gap-1">
                <VBtn
                  icon
                  size="small"
                  variant="text"
                  color="primary"
                >
                  <VIcon icon="tabler-edit" />
                </VBtn>
                <VBtn
                  icon
                  size="small"
                  variant="text"
                  color="error"
                >
                  <VIcon icon="tabler-trash" />
                </VBtn>
              </div>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </template>

    <!-- 데이터 없음 -->
    <VCard v-else>
      <VCardText class="text-center pa-6">
        <VIcon
          icon="tabler-alert-circle"
          size="48"
          color="error"
        />
        <p class="mt-4">
          고객사 정보를 찾을 수 없습니다.
        </p>
        <VBtn
          color="primary"
          :to="{ name: 'review-client' }"
        >
          목록으로 돌아가기
        </VBtn>
      </VCardText>
    </VCard>
  </div>
</template>
